/**
 * capture-client — browser-side Sentry error capture with PII scrubbing.
 *
 * Per `.claude/rules/observability-incident-response-patterns.md`:
 *   - sendDefaultPii MUST be false (set at init time in the app shell)
 *   - Every captureException MUST strip email, phone, IP, and auth tokens
 *   - Only workspace + widget name are attached as Sentry context
 *
 * Usage (from error boundaries):
 *   captureClient(error, { widgetName: 'ExposureWindowTimeline', boundary: 'widget' });
 *
 * This module does NOT call Sentry.init() — that is done once by the app
 * shell (or service worker). If Sentry is not loaded this is a safe no-op.
 */

/** Shape of the optional extra context passed to captureClient. */
export interface ClientCaptureContext {
  /** Widget name — used as Sentry tag, not user-identifiable. */
  widgetName?: string;
  /** Boundary type tag ('widget' | 'page'). */
  boundary?: string;
  /** Workspace ID — opaque UUID, not user-identifiable. */
  workspaceId?: string;
}

// ---------------------------------------------------------------------------
// PII scrubbing (mirrors packages/observability scrubPII)
// ---------------------------------------------------------------------------

const PII_PATTERNS: Array<{ pattern: RegExp; replacement: string }> = [
  { pattern: /[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g, replacement: "[email]" },
  {
    pattern: /(?:\+?966|0)?[5][0-9]{8}|(?:\+?971)[0-9]{8,9}|\+?[1-9][0-9]{7,14}/g,
    replacement: "[phone]",
  },
  { pattern: /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, replacement: "[ip]" },
  { pattern: /([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}/g, replacement: "[ipv6]" },
];

const SENSITIVE_KEYS = new Set([
  "password", "token", "secret", "apikey", "accesstoken",
  "refreshtoken", "authorization", "creditcard", "cvv", "iban",
]);

function scrubString(value: string): string {
  let out = value;
  for (const { pattern, replacement } of PII_PATTERNS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(key.toLowerCase())) {
      out[key] = "[redacted]";
    } else if (typeof value === "string") {
      out[key] = scrubString(value);
    } else if (value !== null && typeof value === "object") {
      out[key] = scrubObject(value as Record<string, unknown>);
    } else {
      out[key] = value;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Capture an error to Sentry with PII scrubbing applied.
 *
 * If Sentry is not initialised (the global is unavailable) this is a no-op.
 * Never throws — error capturing must not disrupt the UI recovery flow.
 */
export function captureClient(
  error: unknown,
  context: ClientCaptureContext = {},
): void {
  try {
    const sentry = (
      typeof window !== "undefined"
        ? (window as unknown as { Sentry?: { captureException: (err: unknown, ctx: unknown) => void } }).Sentry
        : undefined
    );
    if (!sentry) return;

    // Scrub the error message to remove PII.
    const scrubbedError =
      error instanceof Error
        ? Object.assign(
            Object.create(Object.getPrototypeOf(error) as object) as Error,
            error,
            { message: scrubString(error.message) },
          )
        : error;

    sentry.captureException(scrubbedError, {
      tags: scrubObject({
        widgetName: context.widgetName ?? "unknown",
        boundary: context.boundary ?? "widget",
      } as Record<string, unknown>),
      extra: context.workspaceId
        ? { workspaceId: context.workspaceId }
        : undefined,
    });
  } catch {
    // Sentry capture must NEVER throw into the UI recovery path.
  }
}
